/**
 * Security Settings Page
 * Refactored to use modular components
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Shield, Globe, Smartphone, MapPin, Lock, Save } from "lucide-react";
import AppShell from "@/components/layout/AppShell";
import PageHero, { HERO_IMAGES } from "@/components/common/PageHero";
import FeatureGate from "@/components/FeatureGate";

// Import modular components
import {
  IPWhitelistTab,
  MFATab,
  GeofencingTab,
  DeviceBlockingTab
} from "@/components/security-settings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SecuritySettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  
  const [settings, setSettings] = useState({
    ipWhitelistEnabled: false,
    whitelistedIPs: [],
    mfaEnabled: false,
    mfaEmail: "",
    geofencingEnabled: false,
    officeAddress: "",
    officeLatitude: null,
    officeLongitude: null,
    geofenceRadiusMeters: 500,
    geofenceRadiusUnit: "feet",
    geofenceExemptAdmins: true,
    deviceBlockingEnabled: false,
    blockiPhone: false,
    blockiPad: false,
    blockAndroid: false,
    blockDesktop: false
  });
  const [adminNames, setAdminNames] = useState([]);

  useEffect(() => {
    if (user?.role !== "owner") {
      toast.error("Only owners can access security settings");
      navigate("/");
      return;
    }
    fetchSettings();
  }, [user, navigate]);

  // ============== DATA FETCHING ==============
  
  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/security/settings`);
      const { adminNames: names, ...rest } = response.data;
      setSettings(prev => ({ ...prev, ...rest }));
      setAdminNames(names || []);
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load security settings");
    } finally {
      setLoading(false);
    }
  };

  // ============== HANDLERS ==============

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/security/settings`, settings);
      toast.success("Security settings saved");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setSettings({
          ...settings,
          officeLatitude: position.coords.latitude,
          officeLongitude: position.coords.longitude
        });
        toast.success("Location captured");
      },
      (error) => {
        toast.error("Failed to get location: " + error.message);
      }
    );
  };

  const geocodeAddress = async () => {
    if (!settings.officeAddress?.trim()) {
      toast.error("Please enter an address first");
      return;
    }
    
    setGeocodingLoading(true);
    try {
      const encodedAddress = encodeURIComponent(settings.officeAddress.trim());
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1`,
        { headers: { 'User-Agent': 'ChildcareApp/1.0' } }
      );
      
      const data = await response.json();
      if (data && data.length > 0) {
        const result = data[0];
        setSettings({
          ...settings,
          officeLatitude: parseFloat(result.lat),
          officeLongitude: parseFloat(result.lon)
        });
        toast.success("Address geocoded successfully");
      } else {
        toast.error("Could not find coordinates for this address");
      }
    } catch (error) {
      console.error("Geocoding error:", error);
      toast.error("Failed to geocode address");
    } finally {
      setGeocodingLoading(false);
    }
  };

  // ============== RENDER ==============
  
  if (loading) {
    return (
      <AppShell pageTitle="Security Settings" breadcrumb>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full"></div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell pageTitle="Security Settings" breadcrumb>
      {/* Hero Banner */}
      <PageHero 
        imageUrl={HERO_IMAGES.settings}
        title="Security Settings"
        subtitle="Configure access controls and security features"
        altText="Daycare office administration"
      />
      
      <FeatureGate feature="security_settings">
      <div data-testid="security-settings-page" className="relative z-10">
        {/* Header Actions */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-brand-primary/10 rounded-xl">
              <Shield className="w-8 h-8 text-brand-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-ink-main">Security Settings</h2>
              <p className="text-sm text-ink-muted">Configure access controls and security features</p>
            </div>
          </div>
          <Button 
            onClick={saveSettings} 
            disabled={saving} 
            className="bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold rounded-xl shadow-md"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>

        {/* Security Tabs */}
        <Tabs defaultValue="ip" className="space-y-6">
          <TabsList className="grid grid-cols-4 bg-white p-2 rounded-xl shadow-card border border-line">
            <TabsTrigger value="ip" className="data-[state=active]:bg-brand-primary data-[state=active]:text-white rounded-lg">
              <Globe className="w-4 h-4 mr-1" />IP
            </TabsTrigger>
            <TabsTrigger value="mfa" className="data-[state=active]:bg-brand-secondary data-[state=active]:text-white rounded-lg">
              <Lock className="w-4 h-4 mr-1" />MFA
            </TabsTrigger>
            <TabsTrigger value="geo" className="data-[state=active]:bg-brand-highlight data-[state=active]:text-white rounded-lg">
              <MapPin className="w-4 h-4 mr-1" />Geo
            </TabsTrigger>
            <TabsTrigger value="device" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white rounded-lg">
              <Smartphone className="w-4 h-4 mr-1" />Device
            </TabsTrigger>
          </TabsList>

          <TabsContent value="ip">
            <IPWhitelistTab settings={settings} setSettings={setSettings} />
          </TabsContent>

          <TabsContent value="mfa">
            <MFATab settings={settings} setSettings={setSettings} />
          </TabsContent>

          <TabsContent value="geo">
            <GeofencingTab 
              settings={settings} 
              setSettings={setSettings}
              onGetCurrentLocation={getCurrentLocation}
              onGeocodeAddress={geocodeAddress}
              geocodingLoading={geocodingLoading}
              adminNames={adminNames}
            />
          </TabsContent>

          <TabsContent value="device">
            <DeviceBlockingTab settings={settings} setSettings={setSettings} />
          </TabsContent>
        </Tabs>
      </div>
      </FeatureGate>
    </AppShell>
  );
};

export default SecuritySettingsPage;
